"use client";

import { Button } from "@/components/ui/button";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { SearchIcon } from "lucide-react";
import type React from "react";

export type SearchButtonBaseProps = {
  className?: string;
  onSearch: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  isLoading?: boolean;
  /** When true, always shows expanded state with text (controlled by parent) */
  alwaysExpanded?: boolean;
};

/**
 * Base search button component with internationalized text and animations
 * Shared by SearchButton and SearchButtonWithRedirect to avoid duplication
 */
export function SearchButtonBase({
  className,
  onSearch,
  isLoading = false,
  alwaysExpanded = false,
}: SearchButtonBaseProps) {
  const t = useTranslations();
  const { isAnyTriggerOpen } = useSearchTriggerContext();
  const isExpanded = alwaysExpanded || isAnyTriggerOpen;

  return (
    <Button
      variant="brand"
      aria-label={t("searchButton.ariaLabel")}
      data-testid="search-button-base"
      disabled={isLoading}
      className={cn(
        "rounded-full text-white flex items-center justify-center shadow-sm transition-[height,width,padding,gap,background-color,transform,opacity,box-shadow] duration-300 ease-out flex-shrink-0 hover:scale-105 relative overflow-hidden",
        isExpanded
          ? "h-12 px-6 gap-2" // Expanded: 48px with text
          : "h-10 w-10", // Compact: 40px icon-only
        isLoading && "opacity-70 cursor-wait",
        className,
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onSearch}
    >
      {isLoading ? (
        <div
          className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
          role="presentation"
        />
      ) : (
        <SearchIcon className="h-5 w-5 shrink-0" aria-hidden={isExpanded} />
      )}

      {/* Search text (only in expanded state) */}
      {isExpanded && !isLoading && (
        <span className="text-base font-medium whitespace-nowrap animate-in fade-in-0 duration-300">
          {t("searchButton.search")}
        </span>
      )}
    </Button>
  );
}
